import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { TableFooter, TablePagination } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import { styled, useTheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import * as React from "react";
import PayoutApi from "../../../../api/payoutApi";
import CircleAvatar from "../../../../components/CircleAvatar/CircleAvatar";
import CustomTooltip from "../../../../components/CustomTooltip/CustomTooltip";
import StandartButton from "../../../../components/StandartButton/StandartButton";
import {
  BankPayRequest,
  PayoneerPayRequest,
  PayRequestsStatuses,
} from "../../../../types/PayoutTypes";
import { formatDateAndTime } from "../../../../utils/dateActions";
import { getTranslation } from "../../../../utils/getTranslation";
import Notify from "../../../../utils/toaster";
import { getStatusesData } from "../../withdrawalsUtils";
import s from "./BankTable.module.css";

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number,
  ) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? (
          <LastPageIcon color="success" />
        ) : (
          <FirstPageIcon color="success" />
        )}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight color="success" />
        ) : (
          <KeyboardArrowLeft color="success" />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft color="success" />
        ) : (
          <KeyboardArrowRight color="success" />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? (
          <FirstPageIcon color="success" />
        ) : (
          <LastPageIcon color="success" />
        )}
      </IconButton>
    </Box>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#313131",
    color: "white",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#2E2E2E",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "#242424",
    border: 0,
  },
  // hide last border
  "td, th": {
    border: 0,
  },
}));

type BankRequestTableProps = {
  rows: BankPayRequest[];
  page: number;
  rowsPerPage: number;
  setPage: (page: number) => void;
  setRowsPerPage: (page: number) => void;
  count: number;
  setSelectedRequest: React.Dispatch<
    React.SetStateAction<BankPayRequest | undefined>
  >;
  setRequests: React.Dispatch<
    React.SetStateAction<{
      bankRequests: BankPayRequest[];
      payoneerRequests: PayoneerPayRequest[];
    }>
  >;
};
export default function BankRequestTable({
  rows,
  page,
  rowsPerPage,
  setPage,
  setRowsPerPage,
  count,
  setSelectedRequest,
  setRequests,
}: BankRequestTableProps) {
  const token = localStorage.getItem("token");
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const statusesData = getStatusesData(s);

  const changeStatus = async (requstId: string) => {
    if (!token) return;
    const payload = {
      requestId: requstId,
      status: PayRequestsStatuses.PENDING,
    };
    const response = await PayoutApi.changeBankPayRequestStatus(token, payload);
    if (response.status && response.request) {
      setRequests((prev) => ({
        ...prev,
        bankRequests: prev.bankRequests.map((item) => {
          if (item._id === requstId) {
            return {
              ...item,
              status: PayRequestsStatuses.PENDING,
            };
          }
          return item;
        }),
      }));
      const requestData = rows.find((el) => el._id === requstId);
      setSelectedRequest({
        ...requestData!,
        status: PayRequestsStatuses.PENDING,
      });
    }
    if (!response.status && response.message) {
      Notify(response.message);
    }
  };

  if (!rows.length) {
    return <div className={s.noRequest}>No Bank Requests</div>;
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        minWidth: 700,
        width: "100%",
        borderRadius: "20px",
        backgroundColor: "transparent",
      }}
    >
      <Table
        sx={{ minWidth: 700, width: "100%" }}
        aria-label="customized table"
      >
        <TableHead className={s.tableHead}>
          <TableRow>
            <StyledTableCell align="left" width={"25%"}>
              User
            </StyledTableCell>
            <StyledTableCell align="left" width={"30%"}>
              Bank name
            </StyledTableCell>
            <StyledTableCell align="left" width={"30%"}>
              Request Date
            </StyledTableCell>
            <StyledTableCell align="left" width={"15%"}>
              Amount
            </StyledTableCell>
            <StyledTableCell align="left" width={"30%"}>
              Status
            </StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <StyledTableRow
              key={index}
              sx={{
                cursor: "pointer",
              }}
              onClick={() => setSelectedRequest(row)}
            >
              <StyledTableCell align="left">
                <div className={s.userBlock}>
                  <CircleAvatar userId={row.user._id} />
                  <span>
                    {getTranslation(row.user.name, "en") +
                      " " +
                      getTranslation(row.user.surname, "en")}
                  </span>
                </div>
              </StyledTableCell>
              <StyledTableCell align="left" style={{ color: "white" }}>
                {row.payoutInfo.bankName}
              </StyledTableCell>
              <StyledTableCell align="left" style={{ color: "white" }}>
                {row.date
                  ? `${formatDateAndTime(new Date(row.date), "en").formattedDate}, ${formatDateAndTime(new Date(row.date), "en").formattedTime}`
                  : "N/A"}
              </StyledTableCell>
              <StyledTableCell align="left" style={{ color: "white" }}>
                {row.amount + ` (${row.payoutInfo.currency.toUpperCase()})`}
              </StyledTableCell>
              <StyledTableCell align="left">
                <div className={s.spaceBetween}>
                  <CustomTooltip
                    title={row.cancelationReason ?? ""}
                    hideTooltip={row.cancelationReason ? false : true}
                  >
                    <div
                      className={
                        statusesData[row.status as keyof typeof statusesData]
                          .className
                      }
                    >
                      {
                        statusesData[row.status as keyof typeof statusesData]
                          .title
                      }
                    </div>
                  </CustomTooltip>
                  {row.status === PayRequestsStatuses.NEW && (
                    <StandartButton
                      buttonTitle="Get to work"
                      action={() => changeStatus(row._id)}
                    />
                  )}
                </div>
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
        <TableFooter className={s.tableFooter}>
          <TableRow
            sx={{
              minWidth: 700,
              width: "100%",
              border: "none",
            }}
          >
            <TablePagination
              rowsPerPageOptions={[10, 20, 50]}
              colSpan={5}
              count={count}
              rowsPerPage={rowsPerPage}
              page={page}
              slotProps={{
                select: {
                  inputProps: {
                    "aria-label": "rows per page",
                  },
                  native: true,
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
              sx={{ border: "none", color: "white" }}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
